import {Capacitor} from '@capacitor/core';
import {Device} from '@capacitor/device';
import {Health, HealthDataType, HealthSample} from '@capgo/capacitor-health';
import {Api} from '@/services/ApiService'
import {SchultenMedia} from '@/services/DataService'
import {UserProvider} from '@/features/main.types';

// The only health data Coachio ever touches. Read access exclusively: nothing in
// the app writes a sample back, so asking for write permission requested
// something we never exercise, and App Review reads that as a mismatch between
// the declared HealthKit usage and the app (guideline 2.5.1, 1.5.0 build 23).
//
// It was not harmless either. Writing weight is its own switch in the HealthKit
// sheet, and the authorization gate used to require it, so a user who granted
// all five reads but left that switch off was never registered with the backend
// at all - the connection failed silently for someone who had approved
// everything we actually use.
const READ_TYPES: HealthDataType[] = ['steps', 'distance', 'calories', 'heartRate', 'weight'];

interface HealthKitInterface {
  isAvailable(): Promise<boolean>;
  requestAuthorization(callback: (success: boolean, provider: UserProvider) => Promise<void>): Promise<void>;
  checkAuthorization(callback: (success: boolean, provider: UserProvider) => Promise<void>): Promise<void>;
  fetchHealthData(dataType: HealthDataType, startDate?: string, endDate?: string): Promise<HealthSample[]>;
  backgroundFetch(): Promise<void>;
  syncWithBackend(payload: HealthSample[]): Promise<void>;
}

class HealthKitService implements HealthKitInterface {
  // Verify that the native health SDK (Apple Health) is present on the device.
  async isAvailable() {
    if (Capacitor.getPlatform() !== 'ios') {
      return false;
    }

    const result = await Health.isAvailable();

    return result.available;
  }

  // Checks authorization status for the provided data types without prompting the user.
  async checkAuthorization(callback: (success: boolean, provider: UserProvider) => Promise<void>) {

    const result = await Health.checkAuthorization({
      read: READ_TYPES,
    });
    console.log('checkAuthorization', result)

    if (result.readAuthorized?.length > 0) {
      const deviceId = await Device.getId();

      // If user has HealthKit authorization but no backend integration, register the device
      if (!SchultenMedia.User?.integrations?.apple_health) {
        const registered = await this.registerDevice();
        if (!registered) {
          return;
        }
      }

      await callback(true, {
        user_id: deviceId.identifier,
      });
    }
  }

  // Requests read/write access to the provided data types.
  async requestAuthorization(callback: (success: boolean, provider: UserProvider) => Promise<void>) {
    const result = await Health.requestAuthorization({
      read: READ_TYPES,
    });

    if (result.readAuthorized?.length > 0) {
      const [deviceId, deviceInfo] = await Promise.all([
        Device.getId(),
        Device.getInfo(),
      ]);

      try {
        const response = await Api.post(SchultenMedia.API.userProfile.appleHealth.oauth, {
          device_id: deviceId.identifier,
          model: deviceInfo.model,
          platform: deviceInfo.platform,
          operating_system: deviceInfo.operatingSystem,
          os_version: deviceInfo.osVersion,
          manufacturer: deviceInfo.manufacturer,
          is_virtual: deviceInfo.isVirtual,
        });

        await callback(response.success ?? false, {
          user_id: deviceId.identifier,
        });
      } catch (error) {
        console.error("HealthKitService authentication error:", error)
      }
    }
  }

  // Reads samples for the given data type within the specified time frame.
  async fetchHealthData(dataType: HealthDataType, startDate?: string, endDate?: string) {
    const { samples } = await Health.readSamples({
      dataType,
      startDate,
      endDate,
    });

    return samples;
  }

  // No saveHealthData here on purpose: nothing called it, and keeping it would
  // mean keeping the write permission it needs. Bringing it back means asking
  // for write access again in requestAuthorization AND adding
  // NSHealthUpdateUsageDescription back to ios/App/App/Info.plist.

  // Data fetching in background mode
  async backgroundFetch() {
    if (Capacitor.getPlatform() !== 'ios') {
      return;
    }

    const now = new Date();

    // Get start date from last sync or today's start of day
    const appleHealthIntegration = SchultenMedia.User?.integrations?.apple_health;
    let startDate: Date;

    if (appleHealthIntegration?.last_synced_at) {
        console.log('last sync', appleHealthIntegration.last_synced_at)
      // Use last sync date's start of day
      startDate = new Date(appleHealthIntegration.last_synced_at);
      startDate.setHours(0, 0, 0, 0);
    } else {
      // No last sync - use today's start of day
      startDate = new Date();
      startDate.setHours(0, 0, 0, 0);
    }

    // Parallel data fetching - all requests run simultaneously
    const payload = (await Promise.all([
      // Using fetchHealthData method for each data type
      this.fetchHealthData('steps', startDate.toISOString(), now.toISOString()), // Daily steps count
      this.fetchHealthData('heartRate', new Date(now.getTime() - 60 * 60 * 1000).toISOString(), now.toISOString()), // Recent heart rate readings
      this.fetchHealthData('distance', startDate.toISOString(), now.toISOString()), // Walking/running distance
      this.fetchHealthData('calories', startDate.toISOString(), now.toISOString()), // Active calories burned
      this.fetchHealthData('weight', startDate.toISOString(), now.toISOString()), // Weight analysis
    ])).flat();

    await this.syncWithBackend(payload);
  }

  // Registers the current device with the backend (OAuth flow)
  private async registerDevice(): Promise<boolean> {
    console.log("HealthKitService device registration start");
    const [deviceId, deviceInfo] = await Promise.all([
      Device.getId(),
      Device.getInfo(),
    ]);

    try {
      const response = await Api.post(SchultenMedia.API.userProfile.appleHealth.oauth, {
        device_id: deviceId.identifier,
        model: deviceInfo.model,
        platform: deviceInfo.platform,
        operating_system: deviceInfo.operatingSystem,
        os_version: deviceInfo.osVersion,
        manufacturer: deviceInfo.manufacturer,
        is_virtual: deviceInfo.isVirtual,
      });

      console.log("HealthKitService device registration success");

      // Update local user state with the integration data. Guarded on User
      // itself: this runs on startup and on resume, so the profile may not be
      // loaded yet, and the unguarded write threw on that null. The throw
      // landed in the catch below, which reported the registration as failed
      // while the backend had already accepted the device - so the caller
      // skipped its callback and the connection never showed up.
      if (response.success && response.data && SchultenMedia.User) {
        if (!SchultenMedia.User.integrations) {
          SchultenMedia.User.integrations = {};
        }
        SchultenMedia.User.integrations.apple_health = response.data;
      }

      return response.success ?? false;
    } catch (error) {
      console.error("HealthKitService device registration error:", error);
      return false;
    }
  }

  // Synchronizes data with the backend.
  async syncWithBackend(payload: HealthSample[]) {
    try {
        const deviceId = await Device.getId();

        const response = await Api.post(SchultenMedia.API.userProfile.appleHealth.webhook, {
            data: payload,
            device_id: deviceId.identifier
        })

        // Update local user state with sync status from webhook response
        if (response.sync_status && SchultenMedia.User?.integrations?.apple_health) {
          SchultenMedia.User.integrations.apple_health.sync_status = response.sync_status;
          SchultenMedia.User.integrations.apple_health.last_synced_at = response.last_synced_at;
        }

        return response;

    } catch (error) {
        console.error("HealthKitService synchronization error:", error)
    }
  }
}

export default new HealthKitService();